import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon, { IconName } from '../components/Icon';
import '../styles/Dashboard.css';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { taskboardService } from '../services/taskboardService';
import { DashboardSkeleton } from '../components/SkeletonLoader';

interface KpiCard {
  key: string;
  label: string;
  value: number;
  description: string;
  iconName: IconName;
  color: string; // hex used for tinted icon-bg and progress bar
  path?: string;
  /** progress bar percent (0-100). When undefined, we derive from value + total. */
  percent?: number;
  /** optional trend line: positive number = up, negative = down, shown as a chip */
  trend?: number;
}

interface ActivityItem {
  type: string;
  title: string;
  time: string;
  iconName: IconName;
  color: string;
}

const EMPTY_METRICS = {
  notes: 0,
  boards: 0,
  prds: 0,
  whiteboards: 0,
  meetingPlans: 0,
  todo: 0,
  inProgress: 0,
  done: 0,
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState('');
  const [metrics, setMetrics] = useState(EMPTY_METRICS);  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Dashboard | Personal Workspace';
  }, []);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');

    const fetchMetrics = async () => {
      if (!user) return;

      try {
        // Cache key — scoped to user so multi-account users don't collide
        const cacheKey = `dash-cache:${user.id}`;
        const CACHE_TTL_MS = 60_000; // 1 minute — re-fetch in background after

        // ── 1. Read cache synchronously and render immediately (skeleton → real)
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) {
            const cached = JSON.parse(raw) as { ts: number; metrics: typeof EMPTY_METRICS; activities: ActivityItem[] };
            if (Date.now() - cached.ts < CACHE_TTL_MS) {
              setMetrics(cached.metrics);
              setRecentActivity(cached.activities);
              setLoading(false); // skip skeleton entirely
            }
          }
        } catch { /* ignore corrupt cache */ }

        // ── 2. Fire all 8 queries in parallel (was 8 sequential roundtrips)
        const [
          boardsCountRes,
          prdsCountRes,
          whiteboardsCountRes,
          meetingPlansCountRes,
          recentBoardsRes,
          recentPrdsRes,
          recentWhiteboardsRes,
        ] = await Promise.all([
          supabase.from('taskboards').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('documents').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('whiteboards').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('meeting_plans').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('taskboards').select('id, name, updated_at').eq('user_id', user.id).order('updated_at', { ascending: false }).limit(3),
          supabase.from('documents').select('id, title, updated_at').eq('user_id', user.id).order('updated_at', { ascending: false }).limit(3),
          supabase.from('whiteboards').select('id, name, updated_at').eq('user_id', user.id).order('updated_at', { ascending: false }).limit(3),
        ]);

        // ── 3. Kick off the heavy taskboards payload for in-progress counts.
        //         do NOT await before showing metrics — counts are already done.
        //         Fire and update state when it resolves (background refresh).
        const boardsCount = boardsCountRes.count || 0;
        const prdsCount = prdsCountRes.count || 0;
        const whiteboardsCount = whiteboardsCountRes.count || 0;
        const meetingPlansCount = meetingPlansCountRes.count || 0;

        // Optimistic update from counts (todo/inProgress/done stay 0 until loaded)
        setMetrics(prev => ({
          ...prev,
          boards: boardsCount,
          prds: prdsCount,
          whiteboards: whiteboardsCount,
          meetingPlans: meetingPlansCount,
        }));

        // Build activities from the 3 lightweight recent queries (already loaded)
        const recentBoards = recentBoardsRes.data;
        const recentPrds = recentPrdsRes.data;
        const recentWhiteboards = recentWhiteboardsRes.data;
        const activities: ActivityItem[] = [
          ...(recentBoards?.map(b => ({
            type: 'Task Board', title: b.name, time: b.updated_at,
            iconName: 'taskboard' as IconName, color: '#7C66F5',
          })) || []),
          ...(recentPrds?.map(p => ({
            type: 'Document', title: p.title, time: p.updated_at,
            iconName: 'docs' as IconName, color: '#10B981',
          })) || []),
          ...(recentWhiteboards?.map(w => ({
            type: 'Whiteboard', title: w.name, time: w.updated_at,
            iconName: 'whiteboard' as IconName, color: 'var(--accent-color)',
          })) || []),
        ]
          .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
          .slice(0, 5);
        setRecentActivity(activities);

        // ── 4. Persist the snapshot to localStorage so the next visit is instant
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            ts: Date.now(),
            metrics: { ...EMPTY_METRICS, boards: boardsCount, prds: prdsCount, whiteboards: whiteboardsCount, meetingPlans: meetingPlansCount },
            activities,
          }));
        } catch { /* quota / private mode — ignore */ }

        // ── 5. Background fetch for the heavier in-progress task counts
        taskboardService.getTaskBoards().then(taskboards => {
          let todoCount = 0, inProgressCount = 0, doneCount = 0;
          taskboards?.forEach((board: { columns?: { title: string; tasks: unknown[] }[] }) => {
            board.columns?.forEach((col: { title: string; tasks: unknown[] }) => {
              const t = col.title.toLowerCase();
              if (t.includes('to do') || t.includes('todo')) todoCount += col.tasks.length;
              else if (t.includes('in progress') || t.includes('doing')) inProgressCount += col.tasks.length;
              else if (t.includes('done') || t.includes('complete') || t.includes('finished')) doneCount += col.tasks.length;
            });
          });
          setMetrics(prev => ({ ...prev, todo: todoCount, inProgress: inProgressCount, done: doneCount }));
        }).catch(() => { /* non-critical */ });
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [user]);

  // ── KPI cards: 4-column grid with small colored icon, label, big number,
  //    progress bar, and trend chip (the ux/ reference pattern)
  const kpiCards: KpiCard[] = [
    {
      key: 'boards',
      label: 'Task Boards',
      value: metrics.boards,
      description: 'Active boards',
      iconName: 'taskboard',
      color: '#7C66F5',
      path: '/taskboards',
      trend: 12,
    },
    {
      key: 'whiteboards',
      label: 'Whiteboards',
      value: metrics.whiteboards,
      description: 'Created whiteboards',
      iconName: 'whiteboard',
      color: '#22D3EE',
      path: '/whiteboards',
      trend: 8,
    },
    {
      key: 'docs',
      label: 'Documents',
      value: metrics.prds,
      description: 'Documents created',
      iconName: 'docs',
      color: '#10B981',
      path: '/docs-writer',
      trend: 24,
    },
    {
      key: 'meetings',
      label: 'Meeting Plans',
      value: metrics.meetingPlans,
      description: 'Scheduled meetings',
      iconName: 'calendar',
      color: 'var(--accent-color)',
      path: '/meeting-plan',
      trend: -4,
    },
  ];

  const totalTasks = metrics.todo + metrics.inProgress + metrics.done;
  const completionPct = totalTasks > 0
    ? Math.round((metrics.done / totalTasks) * 100)
    : 0;

  // ── Quick action shortcuts in the welcome card
  const quickActions = [
    { label: 'Whiteboard',   iconName: 'whiteboard' as IconName, path: '/whiteboards', color: '#A294FF' },
    { label: 'Task Board',   iconName: 'taskboard'  as IconName, path: '/taskboards',  color: '#FFD56B' },
    { label: 'Write Doc',    iconName: 'docs'       as IconName, path: '/docs-writer',  color: '#5BE3B0' },
    { label: 'Meeting Plan', iconName: 'calendar'   as IconName, path: '/meeting-plan',color: '#8FC8FF' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="dashboard-container">
          <DashboardSkeleton />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard-container fade-in">
        {/* Header */}
        <div className="dashboard-header">
          <h1>{greeting}, {user?.user_metadata?.full_name?.split(' ')[0] || 'User'}</h1>
          <p>Here's what's happening in your workspace today.</p>
        </div>

        {/* ── 4 KPI cards in ux/ reference pattern ──────────────── */}
        <div className="dash-kpi-grid">
          {kpiCards.map(card => {
            const pct = card.percent ?? Math.min(100, card.value * 10);
            const positive = (card.trend ?? 0) >= 0;
            return (
              <button
                key={card.key}
                className="dash-kpi-card"
                onClick={() => card.path && navigate(card.path)}
              >
                <div className="dash-kpi-header">
                  <span
                    className="dash-kpi-icon"
                    style={{ background: `${card.color}1A`, color: card.color }}
                  >
                    <Icon name={card.iconName} size={20} strokeWidth={1.8} />
                  </span>
                  <div className="dash-kpi-label-block">
                    <span className="dash-kpi-label">{card.label}</span>
                    <span className="dash-kpi-value">{card.value.toLocaleString()}</span>
                  </div>
                </div>

                <div
                  className="dash-kpi-progress"
                  style={{ '--kpi-color': card.color } as React.CSSProperties}
                >
                  <div className="dash-kpi-progress-fill" style={{ width: `${pct}%` }} />
                </div>

                <div className="dash-kpi-footer">
                  <span className="dash-kpi-description">{card.description}</span>
                  {card.trend !== undefined && (
                    <span className={`dash-kpi-trend ${positive ? 'up' : 'down'}`}>
                      <Icon
                        name={positive ? 'trendingUp' : 'trendingDown'}
                        size={12}
                        strokeWidth={2.2}
                      />
                      {positive ? '+' : ''}{card.trend}%
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Welcome / completion strip (ux/ "Sales Analytics" inspired) ── */}
        <div className="dashboard-widget welcome-widget">
          <div className="welcome-bg-pattern" />
          <div className="welcome-content">
            <div className="welcome-top">
              <div>
                <h2>Ready to be productive?</h2>
                <p>
                  {totalTasks === 0
                    ? "You have no tasks for today."
                    : <>You have <strong>{metrics.todo}</strong> to do and <strong>{metrics.inProgress}</strong> work-in-progress task{totalTasks !== 1 ? 's' : ''} today.</>
                  }
                </p>
              </div>
              <div className="welcome-completion">
                <span className="welcome-completion-value">{completionPct}%</span>
                <span className="welcome-completion-label">Completed</span>
              </div>
            </div>

            <div className="welcome-progress">
              <div className="welcome-progress-bar" style={{ width: `${completionPct}%` }} />
            </div>

            <div className="welcome-stats-row">
              <div className="welcome-stat">
                <span className="welcome-stat-dot dot-todo" />
                <span className="welcome-stat-label">To Do</span>
                <span className="welcome-stat-value">{metrics.todo}</span>
              </div>
              <div className="welcome-stat">
                <span className="welcome-stat-dot dot-progress" />
                <span className="welcome-stat-label">In Progress</span>
                <span className="welcome-stat-value">{metrics.inProgress}</span>
              </div>
              <div className="welcome-stat">
                <span className="welcome-stat-dot dot-done" />
                <span className="welcome-stat-label">Done</span>
                <span className="welcome-stat-value">{metrics.done}</span>
              </div>
            </div>

            <div className="welcome-actions">
              {quickActions.map(action => (
                <button
                  key={action.label}
                  className="welcome-quick-btn"
                  onClick={() => navigate(action.path)}
                >
                  <span className="welcome-quick-icon" style={{ color: action.color }}>
                    <Icon name={action.iconName} size={18} strokeWidth={1.8} />
                  </span>
                  <span>{action.label}</span>
                  <span className="welcome-quick-chevron">
                    <Icon name="chevronRight" size={14} strokeWidth={2} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Two-column: Activity + Activity Breakdown ──────────── */}
        <div className="dash-bottom-grid">
          <div className="dashboard-widget activity-widget">
            <div className="widget-header">
              <div className="widget-header-title">
                <span className="widget-header-icon" style={{ background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                  <Icon name="bell" size={16} strokeWidth={1.8} />
                </span>
                <h2>Recent Activity</h2>
              </div>
              <span className="widget-header-pill">{recentActivity.length} updates</span>
            </div>
            <div className="activity-list">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, i) => (
                  <div key={i} className="activity-item">
                    <span
                      className="activity-icon"
                      style={{ background: `${activity.color}1A`, color: activity.color }}
                    >
                      <Icon name={activity.iconName} size={18} strokeWidth={1.8} />
                    </span>
                    <div className="activity-details">
                      <div className="activity-title-row">
                        <span className="activity-type-pill" style={{ color: activity.color, background: `${activity.color}1A` }}>
                          {activity.type}
                        </span>
                        <span className="activity-title">Updated "{activity.title}"</span>
                      </div>
                      <div className="activity-time">
                        <Icon name="calendar" size={12} strokeWidth={1.8} />
                        {new Date(activity.time).toLocaleDateString()} · {new Date(activity.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-empty">
                  <span className="activity-empty-icon">
                    <Icon name="bell" size={20} strokeWidth={1.5} />
                  </span>
                  <span>No recent activity</span>
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-widget breakdown-widget">
            <div className="widget-header">
              <div className="widget-header-title">
                <span className="widget-header-icon" style={{ background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                  <Icon name="target" size={16} strokeWidth={1.8} />
                </span>
                <h2>Workspace Breakdown</h2>
              </div>
            </div>
            <div className="breakdown-list">
              {kpiCards.map(card => {
                const total = kpiCards.reduce((sum, c) => sum + c.value, 0) || 1;
                const pct = Math.round((card.value / total) * 100);
                return (
                  <div key={card.key} className="breakdown-item">
                    <div className="breakdown-row">
                      <span className="breakdown-label">
                        <span className="breakdown-icon" style={{ background: `${card.color}1A`, color: card.color }}>
                          <Icon name={card.iconName} size={14} strokeWidth={1.8} />
                        </span>
                        {card.label}
                      </span>
                      <span className="breakdown-value">{card.value}</span>
                    </div>
                    <div className="breakdown-track" style={{ '--kpi-color': card.color } as React.CSSProperties}>
                      <div className="breakdown-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="breakdown-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
